import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ApiRoutes } from 'app/routes/app.routes';
import { HttpService } from '../http/http.service';
import {
  StatisticsPayload,
  StatisticsResponse,
  ClassroomStats,
  CourseStats,
  EvaluationStats,
  LectureStats,
  AssignmentStats,
  TimetableStats,
  StaffStats,
  SeriesStats,
  AcademicLevelStats,
  DepartmentStats,
  SemesterStats,
  SequenceStats,
  SpecializationStats,
  ResultsStats,
  LearnerCourseStats,
  ClassroomCourseStats
} from 'app/interfaces/entity-statistics.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StatisticsService {

  private baseUrl: string = ApiRoutes.api.baseUrl;
  private routes = ApiRoutes.api;

  constructor(private http: HttpClient, private httpService: HttpService) { }

  getAllStatistics() {
    return this.httpService.makeRequest({
      path: this.routes.statistics.all,
      method: 'get',
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  pullStatistics() {
    return new EventSource(`${this.baseUrl}${ApiRoutes.api.statistics.all}`);
  }

  /**
   * Get classroom statistics
   */
  getClassroomStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<ClassroomStats>> {
    return this.httpService.makeRequest({
      path: 'classroom-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get course statistics
   */
  getCourseStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<CourseStats>> {
    return this.httpService.makeRequest({
      path: 'course-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get evaluation statistics
   */
  getEvaluationStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<EvaluationStats>> {
    return this.httpService.makeRequest({
      path: 'evaluation-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get lecture statistics
   */
  getLectureStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<LectureStats>> {
    return this.httpService.makeRequest({
      path: 'lecture-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get assignment statistics
   */
  getAssignmentStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<AssignmentStats>> {
    return this.httpService.makeRequest({
      path: 'assignment-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get timetable statistics
   */
  getTimetableStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<TimetableStats>> {
    return this.httpService.makeRequest({
      path: 'timetable-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get staff statistics
   */
  getStaffStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<StaffStats>> {
    return this.httpService.makeRequest({
      path: 'staff-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get series statistics
   */
  getSeriesStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<SeriesStats>> {
    return this.httpService.makeRequest({
      path: 'series-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get academic level statistics
   */
  getAcademicLevelStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<AcademicLevelStats>> {
    return this.httpService.makeRequest({
      path: 'academic-level-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get department statistics
   */
  getDepartmentStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<DepartmentStats>> {
    return this.httpService.makeRequest({
      path: 'department-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get semester statistics
   */
  getSemesterStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<SemesterStats>> {
    return this.httpService.makeRequest({
      path: 'semester-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get sequence statistics
   */
  getSequenceStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<SequenceStats>> {
    return this.httpService.makeRequest({
      path: 'sequence-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get specialization statistics
   */
  getSpecializationStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<SpecializationStats>> {
    return this.httpService.makeRequest({
      path: 'specialization-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get results statistics
   */
  getResultsStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<ResultsStats>> {
    return this.httpService.makeRequest({
      path: 'results-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get learner course statistics
   */
  getLearnerCourseStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<LearnerCourseStats>> {
    return this.httpService.makeRequest({
      path: 'learner-course-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get classroom course statistics
   */
  getClassroomCourseStatistics(payload: StatisticsPayload): Observable<StatisticsResponse<ClassroomCourseStats>> {
    return this.httpService.makeRequest({
      path: 'classroom-course-statistics',
      method: 'post',
      options: { body: payload },
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }

  /**
   * Get admin dashboard statistics (MOCKED for UI redesign)
   */
  getAdminDashboardStats(): Observable<any> {
    // const mockData = {
    //   data: {
    //     overview: {
    //       totalLearners: 1250,
    //       activeLearners: 1185,
    //       inactiveLearners: 65,
    //       totalStaff: 84,
    //       activeStaff: 79,
    //       totalClassrooms: 42,
    //       activeClassrooms: 40,
    //       totalCourses: 156,
    //       totalDepartments: 8,
    //       totalAcademicLevels: 6,
    //       currentAcademicYear: {
    //         id: 1,
    //         name: '2023-2024',
    //         startDate: '2023-09-01',
    //         endDate: '2024-06-30',
    //         isCurrent: true
    //       },
    //       currentSemester: {
    //         id: 2,
    //         name: 'Second Semester',
    //         isCurrent: true,
    //         sequence: [
    //           { id: 3, name: 'First Sequence', isCurrent: false },
    //           { id: 4, name: 'Second Sequence', isCurrent: true }
    //         ]
    //       }
    //     },
    //     financials: {
    //       totalFeesExpected: 250000000,
    //       totalFeesCollected: 185000000,
    //       totalFeesOutstanding: 65000000,
    //       collectionRate: 74,
    //       paymentStatusBreakdown: {
    //         Paid: { count: 850, amount: 185000000, percentage: 68 },
    //         Pending: { count: 300, amount: 45000000, percentage: 24 },
    //         Overdue: { count: 100, amount: 20000000, percentage: 8 }
    //       },
    //       paymentMethodBreakdown: {
    //         Cash: { count: 400, amount: 80000000, percentage: 43 },
    //         'Bank Transfer': { count: 350, amount: 90000000, percentage: 48 },
    //         'Mobile Money': { count: 100, amount: 15000000, percentage: 9 }
    //       },
    //       recentPayments: [
    //         { id: 101, learnerName: 'Alice Johnson', amount: 50000, paymentDate: '2023-11-20', paymentMethod: 'Bank Transfer', status: 'Paid' },
    //         { id: 102, learnerName: 'Bob Smith', amount: 25000, paymentDate: '2023-11-21', paymentMethod: 'Cash', status: 'Paid' },
    //         { id: 103, learnerName: 'Charlie Brown', amount: 30000, paymentDate: '2023-11-21', paymentMethod: 'Mobile Money', status: 'Pending' },
    //         { id: 104, learnerName: 'David Lee', amount: 100000, paymentDate: '2023-11-22', paymentMethod: 'Bank Transfer', status: 'Paid' },
    //         { id: 105, learnerName: 'Eva Green', amount: 45000, paymentDate: '2023-11-22', paymentMethod: 'Cash', status: 'Paid' }
    //       ],
    //       monthlyRevenue: [
    //         { month: 'Jun', year: 2023, amount: 12000000, count: 150 },
    //         { month: 'Jul', year: 2023, amount: 15000000, count: 180 },
    //         { month: 'Aug', year: 2023, amount: 25000000, count: 300 },
    //         { month: 'Sep', year: 2023, amount: 55000000, count: 650 },
    //         { month: 'Oct', year: 2023, amount: 42000000, count: 500 },
    //         { month: 'Nov', year: 2023, amount: 36000000, count: 420 }
    //       ]
    //     },
    //     academic: {
    //       averageClassPerformance: 13.5,
    //       totalResultsPublished: 4500,
    //       totalResultsPending: 250,
    //       resultsPublicationRate: 94.7,
    //       topPerformingClasses: [
    //         { classroomId: 10, className: 'Form 5 Science', averageScore: 16.2, totalLearners: 35 },
    //         { classroomId: 12, className: 'Upper Sixth Arts', averageScore: 15.8, totalLearners: 28 },
    //         { classroomId: 5, className: 'Form 3 General', averageScore: 14.5, totalLearners: 42 }
    //       ],
    //       performanceDistribution: {
    //         excellent: 15,
    //         good: 45,
    //         average: 30,
    //         belowAverage: 10
    //       },
    //       courseEnrollmentStats: [
    //         { courseId: 101, courseName: 'Mathematics', enrollmentCount: 1250, averageScore: 12.5 },
    //         { courseId: 102, courseName: 'English Language', enrollmentCount: 1250, averageScore: 14.2 },
    //         { courseId: 103, courseName: 'Physics', enrollmentCount: 650, averageScore: 11.8 }
    //       ]
    //     },
    //     assignments: {
    //       totalAssignments: 450,
    //       pendingAssignments: 35,
    //       activeAssignments: 120,
    //       overdueAssignments: 15,
    //       completedAssignments: 280,
    //       averageCompletionRate: 85,
    //       assignmentsByStatus: {
    //         pending: 35,
    //         active: 120,
    //         overdue: 15,
    //         completed: 280
    //       },
    //       recentAssignments: [
    //         { id: 201, name: 'Calculus Homework 3', courseName: 'Mathematics', dueDate: '2023-11-25', status: 'Active', submissionCount: 25 },
    //         { id: 202, name: 'Essay on Shakespeare', courseName: 'Literature', dueDate: '2023-11-28', status: 'Active', submissionCount: 10 },
    //         { id: 203, name: 'Lab Report: Optics', courseName: 'Physics', dueDate: '2023-11-20', status: 'Overdue', submissionCount: 28 }
    //       ]
    //     },
    //     staff: {
    //       totalStaff: 84,
    //       activeStaff: 79,
    //       staffByDepartment: [
    //         { departmentId: 1, departmentName: 'Science', staffCount: 25 },
    //         { departmentId: 2, departmentName: 'Arts', staffCount: 20 },
    //         { departmentId: 3, departmentName: 'Languages', staffCount: 15 },
    //         { departmentId: 4, departmentName: 'Commercial', staffCount: 12 }
    //       ],
    //       staffWithAssignments: 65,
    //       averageAssignmentsPerStaff: 4.5,
    //       staffWorkload: [
    //         { staffId: 501, staffName: 'Mr. John Doe', coursesCount: 5, assignmentsCount: 12, learnersCount: 150 },
    //         { staffId: 502, staffName: 'Mrs. Jane Smith', coursesCount: 4, assignmentsCount: 8, learnersCount: 120 }
    //       ]
    //     },
    //     classrooms: {
    //       totalClassrooms: 42,
    //       activeClassrooms: 40,
    //       averageClassSize: 30,
    //       largestClassSize: 55,
    //       smallestClassSize: 15,
    //       classroomUtilization: [
    //         { classroomId: 1, className: 'Room 101', capacity: 40, currentEnrollment: 35, utilizationRate: 87.5 },
    //         { classroomId: 2, className: 'Room 102', capacity: 40, currentEnrollment: 38, utilizationRate: 95 },
    //         { classroomId: 3, className: 'Lab A', capacity: 30, currentEnrollment: 25, utilizationRate: 83.3 }
    //       ],
    //       classStats: [
    //         { classroomId: 1, className: 'Form 1A', classMaster: { id: 505, name: 'Mr. X' }, learnersCount: 45, averagePerformance: 13.2, feesCollected: 4500000, feesExpected: 5000000 },
    //         { classroomId: 2, className: 'Form 1B', classMaster: { id: 506, name: 'Mrs. Y' }, learnersCount: 42, averagePerformance: 12.8, feesCollected: 4000000, feesExpected: 4800000 }
    //       ]
    //     },
    //     trends: {
    //       learnerGrowth: [
    //         { period: '2021', count: 1000, change: 0 },
    //         { period: '2022', count: 1100, change: 10 },
    //         { period: '2023', count: 1250, change: 13.6 }
    //       ],
    //       staffGrowth: [
    //         { period: '2021', count: 70, change: 0 },
    //         { period: '2022', count: 75, change: 7.1 },
    //         { period: '2023', count: 84, change: 12 }
    //       ],
    //       revenueGrowth: [
    //         { period: '2021', count: 150000000, change: 0 }, // Using count for amount/value consistency in chart
    //         { period: '2022', count: 200000000, change: 33.3 },
    //         { period: '2023', count: 250000000, change: 25 }
    //       ],
    //       performanceTrend: [
    //         { period: 'Term 1', averageScore: 12.5, change: 0 },
    //         { period: 'Term 2', averageScore: 13.0, change: 4 },
    //         { period: 'Term 3', averageScore: 13.5, change: 3.8 }
    //       ]
    //     },
    //     recentActivity: {
    //       newLearners: [
    //         { id: 1001, name: 'New Student 1', matricule: 'MAT001', classroomName: 'Form 1A', enrollmentDate: '2023-11-23' },
    //         { id: 1002, name: 'New Student 2', matricule: 'MAT002', classroomName: 'Form 2B', enrollmentDate: '2023-11-22' }
    //       ],
    //       newStaff: [
    //         { id: 601, name: 'New Teacher 1', department: 'Science', joinDate: '2023-11-15' }
    //       ],
    //       recentResults: [
    //         { id: 701, learnerName: 'Student A', className: 'Form 3A', average: 16.5, rank: 1, publishedDate: '2023-11-24' },
    //         { id: 702, learnerName: 'Student B', className: 'Form 3A', average: 15.8, rank: 2, publishedDate: '2023-11-24' }
    //       ]
    //     },
    //     systemHealth: {
    //       dataCompleteness: {
    //         learnersWithResults: 98,
    //         learnersWithPayments: 95,
    //         classroomsWithAssignments: 100,
    //         coursesWithTeachers: 100
    //       },
    //       alerts: [
    //         { type: 'warning', message: '50 students have pending fees > 1 year', severity: 'medium' },
    //         { type: 'info', message: 'System maintenance scheduled for Saturday', severity: 'low' }
    //       ]
    //     },
    //     generatedAt: new Date().toISOString()
    //   },
    //   message: 'Dashboard stats retrieved successfully',
    //   success: true,
    //   time: new Date().toISOString()
    // };

    // // Return mock data as an Observable with a delay to simulate loading
    // return new Observable(observer => {
    //   setTimeout(() => {
    //     observer.next(mockData);
    //     observer.complete();
    //   }, 1000);
    // });
    return this.httpService.makeRequest({
      path: this.routes.statistics.admin,
      method: 'get',
      loader: { type: 'spinner' },
      isAuthRoutes: true
    });
  }
}

